import { BrokerCommunication, isContractStatus, PAGES } from "@dune/types";
import { Button, PaginatedDataTable, ResizableLayout } from "@dune/ui";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllUnprocessedCommunications } from "../../../api/queries/communications.queries";
import { useMe } from "../../../api/queries/users.queries";
import PanelProvider from "../../../component/layout/PanelProvider";
import { usePanel } from "../../../hooks/usePanel";
import {
	useEmailActions,
	useIsComposerDirty,
	useIsEmailComposerOpen,
	useSelectedEmailId,
} from "../../../stores/email.store";
import { useCurrentPanel } from "../../../stores/panel.store";
import { communicationColumns } from "./CommunicationColumns";
import { RoleEnum } from "../../../../../../packages/vars/src";

const CommunicationListPage = () => {
	const navigate = useNavigate();
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
	});

	const panel = usePanel();
	const currentPanel = useCurrentPanel();
	const selectedEmailId = useSelectedEmailId();
	const { clearComposerState, selectEmail, clearEmailSelection } =
		useEmailActions();
	const isComposerOpen = useIsEmailComposerOpen();
	const isComposerDirty = useIsComposerDirty();
	const { me } = useMe();
	const canCompose = me?.role === RoleEnum.DUNE;

	const {
		data: communicationsData,
		isLoading,
		error,
	} = useGetAllUnprocessedCommunications(pagination);

	useEffect(() => {
		if (
			selectedEmailId &&
			communicationsData?.data &&
			communicationsData.data.length > 0
		) {
			const selectedMail = communicationsData.data.find(
				(mail) => mail.id.toString() === selectedEmailId,
			);
			if (selectedMail) {
				panel.showEmailPreview({
					mail: selectedMail,
					brokerId: 0,
					onClose: () => {
						clearEmailSelection();
					},
				});
			}
		}
	}, [selectedEmailId, communicationsData, panel, clearEmailSelection]);

	const handleNewMail = () => {
		if (isComposerOpen && isComposerDirty) {
			return;
		}
		clearComposerState();

		panel.showEmailComposer({
			entityId: 0,
			entityType: "BROKER",
			replyTo: "contact@duneassurance.com",
			initialData: {
				to: [],
				cc: [],
				subject: "",
				content: "",
			},
		});
	};

	const handleRowClick = (mail: BrokerCommunication) => {
		if (isComposerOpen && isComposerDirty) {
			return;
		}

		if (!mail.relatedEntities || mail.relatedEntities.length === 0) {
			selectEmail(mail.id.toString());
			return;
		}

		const entity = mail.relatedEntities[0];

		if (
			entity.entityType === "PROPOSITION" &&
			entity.entityReference === entity.entityId
		) {
			selectEmail(mail.id.toString());
			return;
		}

		let redirectPath = "";

		switch (entity.entityType) {
			case "BROKER":
				redirectPath = `/brokers/${entity.entityId}?tab=communications`;
				break;
			case "CONSTRUCTION_SITE":
				redirectPath = `/projects/${entity.entityId}?tab=communications`;
				break;
			case "PROPOSITION":
				if (entity.status && isContractStatus(entity.status)) {
					redirectPath = `${PAGES.CONTRACTS.show.withParams(
						entity.entityId.toString(),
					)}?tab=communications`;
				} else {
					redirectPath = `${PAGES.PROPOSALS.show.withParams(
						entity.entityId.toString(),
					)}?tab=communications`;
				}
				break;
			case "GFA":
				redirectPath = `/gfa/${entity.entityId}?tab=communications`;
				break;
			default:
				selectEmail(mail.id.toString());
				return;
		}

		navigate(redirectPath);
	};

	if (error) {
		return (
			<ResizableLayout>
				<ResizableLayout.Main>
					<div className="p-6">
						<div className="text-red-600">
							Erreur lors du chargement des communications: {error.message}
						</div>
					</div>
				</ResizableLayout.Main>
				{currentPanel && (
					<ResizableLayout.Panel>
						<PanelProvider />
					</ResizableLayout.Panel>
				)}
			</ResizableLayout>
		);
	}

	return (
		<ResizableLayout>
			<ResizableLayout.Main>
				<div className="h-full p-6">
					<div className="flex items-center justify-between mb-6">
						<h1 className="text-2xl font-semibold text-gray-900">
							Communications
						</h1>
						<Button onClick={handleNewMail} disabled={!canCompose}>
							<Plus className="mr-2 h-4 w-4" />
							Nouveau
						</Button>
					</div>

					<PaginatedDataTable
						data={communicationsData?.data || []}
						columns={communicationColumns}
						meta={{
							page: communicationsData?.meta?.page || 1,
							limit: communicationsData?.meta?.limit || 10,
							total: communicationsData?.meta?.total || 0,
							totalPages: communicationsData?.meta?.totalPages || 0,
							hasNextPage: communicationsData?.meta?.hasNextPage || false,
							hasPreviousPage:
								communicationsData?.meta?.hasPreviousPage || false,
						}}
						onPageChange={(page) =>
							setPagination((prev) => ({ ...prev, page }))
						}
						onLimitChange={(limit) =>
							setPagination((prev) => ({ ...prev, limit, page: 1 }))
						}
						onRowClick={handleRowClick}
						isLoading={isLoading}
						noResultsText="Aucune communication"
						enableSorting={true}
					/>
				</div>
			</ResizableLayout.Main>
			{currentPanel && (
				<ResizableLayout.Panel>
					<PanelProvider />
				</ResizableLayout.Panel>
			)}
		</ResizableLayout>
	);
};

export default CommunicationListPage;
